/**
 * The till's connection to the server.
 *
 * Every figure on this screen comes from here. Nothing in the POS invents a
 * price, a stock count or a tax rate — a till that computes its own totals is a
 * till that can disagree with the invoice the customer is handed.
 */

import { hostedInTill } from './exit';

/*
 * Empty is not the same as unset, and ?? does not know that.
 *
 * VITE_API_BASE is inlined by Vite at build time. When the build ran without
 * it, the variable was DEFINED and empty — so `?? '/api/v1'` never fired, BASE
 * became '', and every request went to the app's own origin: nginx answered
 * with index.html and the first login parsed HTML as JSON. The symptom was
 * "ارسال کد ناموفق بود", which says nothing about the real cause.
 *
 * So: trim, treat empty as missing, and say so loudly at boot rather than
 * letting each request fail in its own confusing way.
 */
function resolveBase(): string {
  const configured = (import.meta.env.VITE_API_BASE ?? '').trim();
  if (configured !== '') return configured.replace(/\/$/, '');

  // A same-origin default is right for the packaged till, where the wrapper
  // serves the bundle beside the agent. It is wrong for a browser build, and
  // the console message is what turns a mystery into a one-line fix.
  console.error(
    'VITE_API_BASE is not set — falling back to a same-origin /api/v1. ' +
      'If this app is served from a different host than the API, every request will ' +
      'return the HTML page instead of JSON.',
  );
  return '/api/v1';
}

const BASE = resolveBase();

/**
 * apiBase is where every call goes, exported so the live event stream can be
 * hung off the same host.
 *
 * EventSource cannot go through `request` -- it is a long-lived GET the browser
 * owns -- so it has to build its own URL. Reading it from here rather than
 * re-deriving it keeps a till pointed at one agent from streaming from another.
 */
export function apiBase(): string {
  return BASE;
}

export class ServerError extends Error {
  field?: string;
  detail?: string;
  status: number;
  /*
   * The parsed response body, for the handful of errors that carry structure
   * rather than just text.
   *
   * The agent answers some 409s with a whole result object -- the card
   * endpoints do -- and the fields that say what the cashier should do next
   * live there, not in `detail`. Without this they were dropped on the way out
   * of request() and the screen fell back to «خطای ناشناخته» over a message
   * that had already been written for exactly that moment.
   */
  body?: Record<string, unknown>;

  constructor(
    status: number,
    message: string,
    field?: string,
    detail?: string,
    body?: Record<string, unknown>,
  ) {
    super(message);
    this.status = status;
    this.field = field;
    this.detail = detail;
    this.body = body;
  }
}

function token(): string | null {
  return localStorage.getItem('luma.till.token');
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401) {
    /*
     * The session died mid-shift.
     *
     * Handled here rather than left to each caller, because the failure mode
     * otherwise is silent: the screen keeps its last render and every button
     * quietly does nothing, which at a counter with a queue reads as the tablet
     * having frozen.
     */
    localStorage.removeItem('luma.till.token');
    window.dispatchEvent(new CustomEvent('luma.session-expired'));
    throw new ServerError(401, 'نشست منقضی شد — دوباره وارد شوید');
  }

  const text = await res.text();
  const payload = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new ServerError(
      res.status,
      // `message` is included because the agent's richer errors use it: a
      // card 409 explains itself there, and falling through to the generic
      // text threw that explanation away.
      payload.detail ?? payload.error ?? payload.message ?? 'خطای ناشناخته',
      payload.field,
      payload.detail,
      payload,
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b: unknown) => request<T>('POST', p, b),
  patch: <T>(p: string, b: unknown) => request<T>('PATCH', p, b),
};

/*
 * Which of the two things this bundle is pointed at.
 *
 * The routes are identical by design, which is why one bundle covers the cloud
 * and the local agent. What is NOT identical is how a caller proves who it is:
 *
 *   cloud   an OTP user token in the Authorization header
 *   agent   loopback plus an Origin allow-list, and no token at all
 *
/*
 * The agent cannot grow an OTP login, and should not: sending an SMS needs the
 * internet, which is precisely what is missing when a till is running on its
 * own agent. So the cashier asks, once, at boot, rather than carrying a build
 * flag that someone will eventually set wrongly and only discover in a shop.
 */
export type Target = 'cloud' | 'agent' | 'unreachable';

let detected: Target | null = null;

/** The detected target. Null until detectTarget() has answered. */
export function target(): Target | null {
  return detected;
}

/**
 * Asks the API whether it wants a credential.
 *
 * Deliberately a bare fetch rather than request(): a 401 here is the expected
 * answer from the cloud, not a session that died, and routing it through the
 * usual handler would fire luma.session-expired before anyone has signed in.
 *
 * Three answers, because there are three situations and the old code had only
 * two. 'cloud' is concluded ONLY from a server that explicitly asks for a
 * credential -- 401 or 403. Every other failure means we did not reach the API
 * at all, and a login screen cannot help: sending a code needs the very server
 * that just did not answer.
 *
 * This is not hypothetical. A bundle built without VITE_API_BASE falls back to
 * a relative /api/v1; the till wrapper 404s it; the old rule read "not ok" as
 * "cloud" and put a shopkeeper in front of a login whose only button reports
 * «ارسال کد ناموفق بود». The till had decided it was not a till, and said so in
 * the one way that sounds like the SMS gateway is down. An hour went into the
 * gateway before anyone suspected the build.
 *
 * Inside the till window the question does not arise: the wrapper serves this
 * bundle beside the agent, the device is the credential, and no cashier there
 * can ever log in. So a failure there is always "cannot reach the agent".
 */
export async function detectTarget(): Promise<Target> {
  try {
    const res = await fetch(BASE + '/till/session', { headers: { Accept: 'application/json' } });
    if (res.ok) {
      detected = 'agent';
    } else if (hostedInTill()) {
      detected = 'unreachable';
    } else {
      detected = res.status === 401 || res.status === 403 ? 'cloud' : 'unreachable';
    }
  } catch {
    // Nothing answered. Not even a login could be sent from here.
    detected = 'unreachable';
  }
  return detected;
}
